import React, { useState, useEffect, useRef } from 'react';
import { NotificationProps } from './NotificationTypes';
import { CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useCommonTranslation } from '../../modules/i18n/hooks/useTranslation';

interface NotificationItemProps {
  notification: NotificationProps;
  onClose: () => void;
}

const NotificationItemBase: React.FC<NotificationItemProps> = ({ notification, onClose }) => {
  const { t } = useCommonTranslation();
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Cleanup function
  const cleanup = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Xử lý animation khi đóng
  const handleClose = () => {
    if (!isMountedRef.current) return;

    setIsExiting(true);
    cleanup();

    timeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        onClose();
      }
    }, 300); // Đợi animation kết thúc
  };

  // Progress bar animation
  useEffect(() => {
    if (notification.autoClose && notification.duration) {
      intervalRef.current = setInterval(() => {
        if (!isMountedRef.current) return;

        setProgress((prev) => {
          const newProgress = prev - 100 / (notification.duration! / 50);
          return newProgress <= 0 ? 0 : newProgress;
        });
      }, 50);
    }

    return cleanup;
  }, [notification.autoClose, notification.duration]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, []);

  // Cấu hình icon và màu sắc theo loại notification
  const getTypeConfig = () => {
    switch (notification.type) {
      case 'success':
        return {
          icon: <CheckCircleIcon className='h-6 w-6 text-white' />,
          bgColor: 'bg-green-500 dark:bg-green-600',
          shadowColor: 'shadow-green-500/20 dark:shadow-green-900/40'
        };
      case 'error':
        return {
          icon: <ExclamationCircleIcon className='h-6 w-6 text-white' />,
          bgColor: 'bg-red-500 dark:bg-red-600',
          shadowColor: 'shadow-red-500/20 dark:shadow-red-900/40'
        };
      case 'warning':
        return {
          icon: <ExclamationCircleIcon className='h-6 w-6 text-white' />,
          bgColor: 'bg-yellow-500 dark:bg-yellow-600',
          shadowColor: 'shadow-yellow-500/20 dark:shadow-yellow-900/40'
        };
      case 'info':
      default:
        return {
          icon: <InformationCircleIcon className='h-6 w-6 text-white' />,
          bgColor: 'bg-blue-500 dark:bg-blue-600',
          shadowColor: 'shadow-blue-500/20 dark:shadow-blue-900/40'
        };
    }
  };

  const { icon, bgColor, shadowColor } = getTypeConfig();

  return (
    <div
      className={`
                w-auto min-w-min max-w-sm ${bgColor} rounded-lg shadow-lg ${shadowColor}
                transform transition-all duration-300 ease-in-out
                ${isExiting ? '-translate-y-4 opacity-0' : 'opacity-100'} 
            `}
    >
      <div className='flex items-start gap-3 p-4'>
        {/* Icon */}
        <div className='flex-shrink-0'>{icon}</div>

        {/* Nội dung */}
        <div className='min-w-0 flex-1'>
          <p className='line-clamp-2 text-sm font-medium text-white'>{notification.title}</p>
          {notification.message && <p className='mt-1 line-clamp-3 text-xs text-white/90'>{notification.message}</p>}
        </div>

        {/* Nút đóng */}
        <button
          onClick={handleClose}
          className='flex-shrink-0 text-white/70 transition-colors hover:text-white focus:outline-none'
          aria-label={t('notification.close')}
        >
          <XMarkIcon className='h-5 w-5' />
        </button>
      </div>

      {/* Progress bar cho auto close */}
      {notification.autoClose && notification.duration && (
        <div className='h-1 overflow-hidden rounded-b-lg bg-white/20'>
          <div className='h-full rounded-b-lg bg-white transition-all duration-75 ease-linear' style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
};

export const NotificationItem = React.memo(NotificationItemBase);
